// Serial numbers (IMEI): a product's units, writing one off, and finding who
// bought a unit.
import { normalizeSerial } from '@ims/core'
import type { ProductRow } from '@ims/data'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Input,
  Modal,
  TD,
  TH,
  Table,
  useToast,
} from '@ims/ui'
import { Search } from '@ims/ui/icons'
import { useState } from 'react'
import { Link } from 'react-router'
import { useLookups } from '../components.tsx'
import { errorMessage, useLive } from '../hooks.ts'
import { todayIso } from '../lib/format.ts'
import { insertOp, newMovement, serialOp, type SerialRow } from '../lib/rows.ts'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'
import { finePointer } from '../lib/pointer.ts'
import { ScanButton } from '../scanner.tsx'

const STATUS: Record<
  SerialRow['status'],
  { label: string; tone: 'success' | 'neutral' | 'danger' }
> = {
  in_stock: { label: 'In stock', tone: 'success' },
  sold: { label: 'Sold', tone: 'neutral' },
  written_off: { label: 'Written off', tone: 'danger' },
}

interface UnitInfo {
  unit: SerialRow
  product: ProductRow | null
  /** The sale of a sold unit. */
  sale: {
    invoiceId: string
    number: string | null
    date: string
    customer: string | null
  } | null
}

/** Units with their product and (for sold ones) the invoice that sold them. */
async function describeUnits(
  repo: ReturnType<typeof useRepo>,
  units: readonly SerialRow[],
): Promise<UnitInfo[]> {
  const out: UnitInfo[] = []
  for (const unit of units) {
    const product = await repo.get('products', unit.product_id)
    let sale: UnitInfo['sale'] = null
    if (unit.sold_line_id) {
      const line = await repo.get('sales_invoice_lines', unit.sold_line_id)
      const invoice = line ? await repo.get('sales_invoices', line.invoice_id) : null
      if (invoice)
        sale = {
          invoiceId: invoice.id,
          number: invoice.number,
          date: invoice.invoice_date,
          customer: invoice.customer_name,
        }
    }
    out.push({ unit, product, sale })
  }
  return out
}

function warrantyText(unit: SerialRow): string | null {
  if (!unit.warranty_until || unit.status !== 'sold') return null
  return unit.warranty_until >= todayIso()
    ? `Warranty until ${unit.warranty_until}`
    : `Warranty ended ${unit.warranty_until}`
}

/** A serial-tracked product's units on its page. */
export function SerialsCard({ product }: { product: ProductRow }) {
  const repo = useRepo()
  const lookups = useLookups()
  const { current } = useWorkspace()
  const canEdit = useCan('staff')
  const toast = useToast()
  const [writingOff, setWritingOff] = useState<SerialRow | null>(null)
  const [busy, setBusy] = useState(false)
  const units =
    useLive(
      async (r) =>
        describeUnits(
          r,
          (
            await r.list('product_serials', {
              where: { product_id: product.id },
              orderBy: { column: 'serial' },
              limit: 5000,
            })
          ).rows,
        ),
      [product.id],
      ['product_serials', 'sales_invoices'],
    ).data ?? []
  const inStock = units.filter((u) => u.unit.status === 'in_stock').length

  const writeOff = async (unit: SerialRow) => {
    if (!current) return
    const location = unit.location_id ?? lookups.activeLocations[0]?.id
    if (!location) return
    setBusy(true)
    try {
      const ctx = { tenantId: current.tenant_id }
      await repo.commit([
        insertOp(
          'stock_movements',
          newMovement(ctx, {
            product_id: unit.product_id,
            location_id: location,
            type: 'adjustment',
            qty_milli: -1000,
            notes: `Written off: ${unit.serial}`,
          }),
        ),
        serialOp(ctx, unit.product_id, unit.serial, unit, { status: 'written_off' }),
      ])
      toast.success(`${unit.serial} written off`)
      setWritingOff(null)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="mt-4 p-0">
      <h2 className="flex items-center justify-between border-b border-border px-5 py-3 font-semibold">
        Serial numbers
        <span className="text-sm font-normal text-muted">
          {inStock} in stock · {units.length} recorded
        </span>
      </h2>
      {units.length === 0 ? (
        <EmptyState title="No serial numbers yet" description="Receive stock to record them." />
      ) : (
        <Table>
          <thead>
            <tr>
              <TH>Serial / IMEI</TH>
              <TH>Status</TH>
              <TH>Where / who</TH>
              <TH />
            </tr>
          </thead>
          <tbody>
            {units.map(({ unit, sale }) => (
              <tr key={unit.id} className="border-t border-border">
                <TD className="font-mono">{unit.serial}</TD>
                <TD>
                  <Badge tone={STATUS[unit.status].tone}>{STATUS[unit.status].label}</Badge>
                </TD>
                <TD className="text-sm text-muted">
                  {unit.status === 'in_stock' && lookups.location(unit.location_id)?.name}
                  {unit.status === 'sold' && sale && (
                    <>
                      <Link
                        to={`/invoices/${sale.invoiceId}`}
                        className="text-primary hover:underline"
                      >
                        {sale.number ?? 'Invoice'}
                      </Link>{' '}
                      · {sale.date}
                      {sale.customer && ` · ${sale.customer}`}
                      {warrantyText(unit) && <div>{warrantyText(unit)}</div>}
                    </>
                  )}
                </TD>
                <TD align="right">
                  {canEdit && unit.status === 'in_stock' && (
                    <Button size="sm" variant="ghost" onClick={() => setWritingOff(unit)}>
                      Write off
                    </Button>
                  )}
                </TD>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <ConfirmDialog
        open={writingOff !== null}
        title="Write off this unit?"
        message={`${writingOff?.serial ?? ''} leaves stock (lost, damaged or sent back).`}
        confirmLabel="Write off"
        danger
        busy={busy}
        onClose={() => setWritingOff(null)}
        onConfirm={() => writingOff && void writeOff(writingOff)}
      />
    </Card>
  )
}

/** Looks up a serial / IMEI: which product, whether it's in stock, who bought it. */
export function FindSerialDialog({ onClose }: { onClose: () => void }) {
  const lookups = useLookups()
  const [text, setText] = useState('')
  const [query, setQuery] = useState('')
  const results = useLive(
    async (r) =>
      query
        ? describeUnits(
            r,
            (await r.list('product_serials', { where: { serial: query }, limit: 20 })).rows,
          )
        : null,
    [query],
    ['product_serials', 'sales_invoices'],
  ).data
  return (
    <Modal open onClose={onClose} title="Find a serial number" size="lg" unsaved={false}>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          setQuery(normalizeSerial(text))
        }}
      >
        <div className="relative min-w-0 flex-1">
          <Input
            aria-label="Serial / IMEI"
            placeholder="Scan or type a serial / IMEI"
            value={text}
            autoFocus={finePointer}
            onChange={(e) => setText(e.target.value)}
            className="pr-10"
          />
          <ScanButton
            mode="single"
            title="Scan a serial / IMEI"
            label="Scan a serial with the camera"
            onCode={(code) => {
              setText(code)
              setQuery(normalizeSerial(code))
              return { ok: true, message: code }
            }}
            className="absolute right-0.5 top-1/2 -translate-y-1/2"
          />
        </div>
        <Button type="submit" icon={<Search className="size-4" />}>
          Find
        </Button>
      </form>
      {results && results.length === 0 && (
        <p className="mt-4 text-sm text-muted">No unit with serial {query}.</p>
      )}
      {results && results.length > 0 && (
        <ul className="mt-4 divide-y divide-border rounded-xl border border-border">
          {results.map(({ unit, product, sale }) => (
            <li key={unit.id} className="space-y-1 p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Link
                  to={`/products/${unit.product_id}`}
                  onClick={onClose}
                  className="font-medium text-primary hover:underline"
                >
                  {product?.name ?? 'Unknown product'}
                </Link>
                <Badge tone={STATUS[unit.status].tone}>{STATUS[unit.status].label}</Badge>
              </div>
              <div className="font-mono text-muted">{unit.serial}</div>
              {unit.status === 'in_stock' && (
                <div className="text-muted">At {lookups.location(unit.location_id)?.name}</div>
              )}
              {sale && unit.status === 'sold' && (
                <div className="text-muted">
                  Sold on{' '}
                  <Link
                    to={`/invoices/${sale.invoiceId}`}
                    onClick={onClose}
                    className="text-primary hover:underline"
                  >
                    {sale.number ?? 'invoice'}
                  </Link>{' '}
                  · {sale.date}
                  {sale.customer && ` · ${sale.customer}`}
                  {warrantyText(unit) && ` · ${warrantyText(unit)}`}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}
